import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { Route, Navigate, Routes, useNavigate, useLocation } from 'react-router-dom';
import config from './config';
import { fetchLogout, fetchUserProfile } from './services/api';
import { getOnboarding, updateOnboarding } from './services/onboarding';
import { markCurrentSessionInternal } from './services/analytics';
import { clearMessageDetailsCache } from './hooks/useMessageDetails';
import { authReturnTo, authRoute } from './utils/authNavigation';
import { isExperienceOnboardingEnabled, postAuthDestination, defaultOnboardingDestination } from './utils/onboardingNavigation';
import { clearPendingRegistration } from './utils/pendingRegistration';
import { clearChatRecovery } from './utils/chatDrafts';
import AppLoading from './components/Common/AppLoading';
// These shared styles have one eager owner so lazy routes cannot impose
// contradictory CSS extraction order. Keep their contents and order intact.
import './components/Common/WorkspaceShell.css';
import './components/AssistantMessage.css';
import './components/Agent/StopStatus.css';

const ChatContainer = lazy(() => import('./components/Chat/ChatContainer'));
const SharedThreadView = lazy(() => import('./components/Chat/SharedThreadView'));
const LoginForm = lazy(() => import('./components/LoginForm'));
const RegistrationForm = lazy(() => import('./components/RegistrationForm'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const AdminAnalyticsPage = lazy(() => import('./components/AdminAnalyticsPage'));
const ProjectsPage = lazy(() => import('./components/Projects/ProjectsPage'));
const CreateProjectPage = lazy(() => import('./components/Projects/CreateProjectPage'));
const ProjectWorkspacePage = lazy(() => import('./components/Projects/ProjectWorkspacePage'));
const MicaPage = lazy(() => import('./components/Mica/MicaPage'));
const PricingPage = lazy(() => import('./components/Pricing/PricingPage'));
const ActivationPage = lazy(() => import('./components/Pricing/ActivationPage'));
const CreditWalletPage = lazy(() => import('./components/Credits/CreditWalletPage'));
const PasswordReset = lazy(() => import('./components/PasswordReset'));
const PrivacyPolicy = lazy(() => import('./components/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/Legal/TermsOfService'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const OnboardingPage = lazy(() => import('./components/Onboarding/OnboardingPage'));
// A speculative import must never mount sockets or dispatch a model request.
const loadAgentPage = () => import('./components/Agent/AgentPage');
const AgentPage = lazy(loadAgentPage);
const V9Home = lazy(() => import('./components/V9/V9Home'));
const DownloadsPage = lazy(() => import('./components/Downloads/DownloadsPage'));
const V10Home = lazy(() => import('./components/V10/V10Home'));
const FounderOnboardingForm = lazy(() => import('./components/FounderOnboardingForm'));

const getAuthScope = payload => {
  const user = payload?.user;
  if (typeof user === 'string' && user.trim()) return user.trim();
  if (user && typeof user === 'object') {
    const identity = user.user_id || user.id || user.username || user.email;
    if (typeof identity === 'string' && identity.trim()) return identity.trim();
    if (typeof identity === 'number' && Number.isFinite(identity)) return String(identity);
  }
  const fallback = payload?.user_id || payload?.username || payload?.email;
  return typeof fallback === 'string' && fallback.trim() ? fallback.trim() : null;
};
const EMPTY_ONBOARDING = { available: false, onboarding: null };

export default function App() {
  const enabled = isExperienceOnboardingEnabled();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('none');
  const [subscriptionStatus, setSubscriptionStatus] = useState('none');
  const [authScope, setAuthScope] = useState(null);
  const [authSessionKey, setAuthSessionKey] = useState(0);
  const [onboarding, setOnboarding] = useState(EMPTY_ONBOARDING);
  const onboardingRef = useRef(EMPTY_ONBOARDING);
  const authScopeRef = useRef(null);
  const authSessionActiveRef = useRef(false);
  const generationRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  // Only the landing route, never /downloads/* artifacts or lookalike paths.
  const isPublicDownloads = ['/downloads', '/downloads/'].includes(location.pathname);
  const authIdentity = authScope ? `${authScope}:${authSessionKey}` : null;

  const applyOnboarding = useCallback(result => {
    onboardingRef.current = result;
    setOnboarding(result);
  }, []);
  const getPostLoginPath = useCallback((record = onboardingRef.current.onboarding, available = onboardingRef.current.available) => enabled
    ? (!available && location.state?.postRegistration
      ? authReturnTo({ search: location.search, state: location.state }, null) || '/onboarding'
      : postAuthDestination({ search: location.search, state: location.state }, record))
    : new URLSearchParams(location.search).get('intent') === 'activate' ? '/activate' : authReturnTo({ search: location.search, state: location.state }), [enabled, location.search, location.state]);
  const clearAuth = useCallback(() => {
    authSessionActiveRef.current = false;
    authScopeRef.current = null;
    clearMessageDetailsCache();
    setIsLoggedIn(false); setAuthScope(null);
    setSubscriptionTier('none'); setSubscriptionStatus('none');
    applyOnboarding(EMPTY_ONBOARDING);
  }, [applyOnboarding]);
  const applyIdentity = useCallback(payload => {
    const nextScope = getAuthScope(payload);
    if (!authSessionActiveRef.current || authScopeRef.current !== nextScope) {
      clearMessageDetailsCache();
      setAuthSessionKey(value => value + 1);
    }
    authSessionActiveRef.current = true;
    authScopeRef.current = nextScope;
    setAuthScope(nextScope); setIsLoggedIn(true);
  }, []);

  useEffect(() => {
    if (location.pathname === '/agent') void loadAgentPage().catch(() => {});
    // A static download catalog must not depend on cookie/profile availability.
    // Do not mark auth as checked: leaving for a protected route still probes it.
    if (isPublicDownloads) return undefined;
    if (['/register', '/reset-password', '/privacy', '/terms'].includes(location.pathname)) {
      setIsAuthChecked(true);
      return undefined;
    }
    let alive = true;
    const controller = new AbortController();
    const generation = generationRef.current;
    const current = () => alive && generationRef.current === generation;
    const check = async () => {
      try {
        const response = await fetch(`${config.apiV2Url}/check-auth`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' }, signal: controller.signal });
        if (!current()) return;
        if (!response.ok) { clearAuth(); return; }
        const payload = typeof response.clone === 'function' ? await response.clone().json().catch(() => ({})) : {};
        if (payload.authenticated === false || (enabled && payload.authenticated !== true)) { clearAuth(); return; }
        let profile = null;
        try {
          const profilePromise = fetchUserProfile();
          if (location.pathname === '/agent') void import('./components/Common/MarkdownContent').catch(() => {});
          profile = await profilePromise;
        } catch (_) { /* Profile errors must not fabricate activation. */ }
        let preferenceResult = EMPTY_ONBOARDING;
        if (enabled) {
          try { preferenceResult = await getOnboarding({ signal: controller.signal }); }
          catch (error) {
            if ([401, 403].includes(error.status)) { if (current()) clearAuth(); return; }
          }
        }
        if (!current()) return;
        applyOnboarding(preferenceResult);
        applyIdentity(payload);
        setSubscriptionTier(profile?.user?.subscription_tier || 'none');
        setSubscriptionStatus(profile?.user?.subscription_status || 'none');
        if (payload.user === 'henosis') markCurrentSessionInternal();
        if (['/', '/login'].includes(location.pathname)) navigate(getPostLoginPath(preferenceResult.onboarding), { replace: true });
      } catch (error) {
        if (current() && error.name !== 'AbortError') clearAuth();
      } finally { if (current()) setIsAuthChecked(true); }
    };
    void check();
    const interval = setInterval(check, 1800000);
    return () => { alive = false; controller.abort(); clearInterval(interval); };
  }, [applyIdentity, applyOnboarding, clearAuth, enabled, getPostLoginPath, isPublicDownloads, location.pathname, navigate]);

  const handleLogin = useCallback(async loginData => {
    const generation = ++generationRef.current;
    applyOnboarding(EMPTY_ONBOARDING);
    try {
      if (loginData?.device_id && loginData?.device_name) {
        try { localStorage.setItem('deviceId', loginData.device_id); localStorage.setItem('deviceName', loginData.device_name); } catch (_) {}
      }
      let profile = null;
      try { profile = await fetchUserProfile(); } catch (_) {}
      let preferenceResult = EMPTY_ONBOARDING;
      if (enabled) {
        try { preferenceResult = await getOnboarding(); }
        catch (error) { if ([401, 403].includes(error.status)) throw error; }
      }
      if (generationRef.current !== generation) return;
      applyOnboarding(preferenceResult);
      applyIdentity(loginData);
      clearPendingRegistration();
      setSubscriptionTier(profile?.user?.subscription_tier || loginData?.subscriptionTier || 'none');
      setSubscriptionStatus(profile?.user?.subscription_status || loginData?.subscriptionStatus || 'none');
      // Unknown/missing preference endpoint must never be presented as a saved
      // choice. New registrations get an explicit recovery surface instead.
      const destination = enabled && !preferenceResult.available && location.state?.postRegistration
        ? authReturnTo(location, null) || '/onboarding'
        : getPostLoginPath(preferenceResult.onboarding);
      navigate(destination, { replace: true });
    } catch (error) {
      if (generationRef.current === generation) clearAuth();
      throw error;
    }
  }, [applyIdentity, applyOnboarding, clearAuth, enabled, getPostLoginPath, location, navigate]);

  const handleLogout = useCallback(async () => {
    // Clear local identity first so late profile/preferences responses cannot
    // rehydrate the account being signed out.
    generationRef.current += 1;
    if (enabled && authScopeRef.current) clearChatRecovery(authScopeRef.current);
    clearAuth(); clearPendingRegistration();
    try { localStorage.removeItem('deviceId'); localStorage.removeItem('deviceName'); sessionStorage.removeItem('henosis.selectedModel'); } catch (_) {}
    try { await fetchLogout(); } catch (_) { /* Server sign-out remains best effort, as before. */ }
    navigate('/login', { replace: true });
  }, [clearAuth, enabled, navigate]);

  const saveOnboarding = useCallback(async patch => {
    const generation = generationRef.current;
    const scope = authScopeRef.current;
    try {
      const result = await updateOnboarding(patch);
      if (generationRef.current !== generation || authScopeRef.current !== scope || !authSessionActiveRef.current) throw new Error('Account changed while saving preferences');
      if (result.available) applyOnboarding(result);
      return result;
    } catch (error) {
      if ([401, 403].includes(error.status)) await handleLogout();
      throw error;
    }
  }, [applyOnboarding, handleLogout]);

  if (!isAuthChecked && !isPublicDownloads) return <AppLoading />;
  const requireAuth = (element, legacyReturn = null) => isLoggedIn ? element : <Navigate to={enabled
    ? authRoute('/login', { search: `?returnTo=${encodeURIComponent(`${location.pathname}${location.search}${location.hash}`)}` })
    : legacyReturn ? `/login?returnTo=${legacyReturn}` : '/login'} replace />;
  const defaultDestination = enabled ? defaultOnboardingDestination(onboarding.onboarding) : '/chat';
  const onboardingPage = mode => requireAuth(enabled ? <OnboardingPage mode={mode} preferences={onboarding.onboarding} available={onboarding.available} onSave={saveOnboarding} onLogout={handleLogout} /> : <Navigate to="/chat" replace />);
  return <Suspense fallback={<AppLoading />}><Routes>
    <Route path="/login" element={isLoggedIn ? <Navigate to={getPostLoginPath()} replace /> : <LoginForm onLogin={handleLogin} onSwitchToRegister={() => navigate(authRoute('/register', location))} />} />
    <Route path="/register" element={isLoggedIn ? <Navigate to={defaultDestination} replace /> : <RegistrationForm onSwitchToLogin={() => navigate(authRoute('/login', location))} />} />
    <Route path="/reset-password" element={isLoggedIn ? <Navigate to={defaultDestination} replace /> : <PasswordReset onLogin={handleLogin} onSwitchToRegister={() => navigate(authRoute('/register', location))} />} />
    <Route path="/privacy" element={<PrivacyPolicy />} /><Route path="/terms" element={<TermsOfService />} />
    <Route path="/downloads" element={<DownloadsPage />} />
    <Route path="/founder-onboarding" element={<FounderOnboardingForm />} />
    <Route path="/pricing" element={<PricingPage currentTier={subscriptionTier} isLoggedIn={isLoggedIn} onUpgrade={() => { if (!isLoggedIn) navigate('/login'); }} />} />
    <Route path="/activate" element={requireAuth(<ActivationPage subscriptionTier={subscriptionTier} subscriptionStatus={subscriptionStatus} />, '/activate')} />
    <Route path="/credits" element={requireAuth(<CreditWalletPage key={authIdentity} authScope={authScope} />, '/credits')} />
    <Route path="/profile" element={requireAuth(<Profile onLogout={handleLogout} subscriptionTier={subscriptionTier} />)} />
    <Route path="/agent" element={requireAuth(<AgentPage onLogout={handleLogout} authScope={authScope} authIdentity={authIdentity} />, '/agent')} />
    <Route path="/mica" element={requireAuth(<MicaPage key={authIdentity} authScope={authScope} onLogout={handleLogout} />, encodeURIComponent(`${location.pathname}${location.search}${location.hash}`))} />
    <Route path="/mica/:memoryUid" element={requireAuth(<MicaPage key={authIdentity} authScope={authScope} onLogout={handleLogout} />, encodeURIComponent(`${location.pathname}${location.search}${location.hash}`))} />
    <Route path="/chat" element={requireAuth(<ChatContainer key={authIdentity} onLogout={handleLogout} subscriptionTier={subscriptionTier} authIdentity={authIdentity} authScope={authScope} isLanding onboarding={enabled ? onboarding.onboarding : null} onOnboardingChange={enabled ? saveOnboarding : undefined} />)} />
    <Route path="/chat/:threadId" element={requireAuth(<ChatContainer key={authIdentity} onLogout={handleLogout} subscriptionTier={subscriptionTier} authIdentity={authIdentity} authScope={authScope} isLanding={false} />)} />
    <Route path="/onboarding" element={onboardingPage('choose')} /><Route path="/onboarding/cli" element={onboardingPage('cli')} /><Route path="/onboarding/providers" element={onboardingPage('providers')} />
    <Route path="/dashboard" element={requireAuth(<Dashboard onLogout={handleLogout} subscriptionTier={subscriptionTier} />)} />
    <Route path="/admin/analytics" element={requireAuth(<AdminAnalyticsPage onLogout={handleLogout} />, '/admin/analytics')} />
    <Route path="/projects" element={requireAuth(<ProjectsPage onLogout={handleLogout} subscriptionTier={subscriptionTier} />)} />
    <Route path="/projects/new" element={requireAuth(<CreateProjectPage subscriptionTier={subscriptionTier} />)} />
    <Route path="/projects/:projectId" element={requireAuth(<ProjectWorkspacePage />)} />
    <Route path="/shared/:threadId" element={<SharedThreadView subscriptionTier={subscriptionTier} isLoggedIn={isLoggedIn} />} />
    <Route path="/" element={isLoggedIn ? <Navigate to={getPostLoginPath()} replace /> : <V9Home />} />
    <Route path="/v10/landing" element={<V10Home />} />
    <Route path="*" element={<Navigate to={isLoggedIn ? defaultDestination : '/login'} replace />} />
  </Routes></Suspense>;
}
